import { useMemo, useState } from 'react'
import { ChevronRight, Layers, PlaneTakeoff, PlaneLanding, RotateCcw, Wand2, Zap } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  useEventPlannerStore,
  selectTotal,
  selectUnprocessed,
  selectPlacedCount,
  selectOverflow,
  selectCapacity,
  selectPeakConcurrent,
} from '../store/eventPlannerStore'
import { ArrivalInquiryRow } from './ArrivalInquiryRow'
import { ZoneConfirmList } from './ZoneConfirmList'
import { DEMO_SCENARIOS } from '../data/scenarios'
import type { PlacementAlgorithm } from '../engine/types'

const allocId = (scheduleId: string) => `evt-${scheduleId}`

interface PlannerDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function PlannerDrawer({ open, onOpenChange }: PlannerDrawerProps) {
  const [blockSize, setBlockSize] = useState(10)
  const [showZones, setShowZones] = useState(false)

  const ready = useEventPlannerStore((s) => s.ready)
  const orderedScheduleIds = useEventPlannerStore((s) => s.orderedScheduleIds)
  const inquiriesById = useEventPlannerStore((s) => s.inquiriesById)
  const placements = useEventPlannerStore((s) => s.placements)
  const algorithm = useEventPlannerStore((s) => s.algorithm)
  const algorithmDirty = useEventPlannerStore((s) => s.algorithmDirty)
  const activeScenarioId = useEventPlannerStore((s) => s.activeScenarioId)
  const customScenarios = useEventPlannerStore((s) => s.customScenarios)
  const scenarioName =
    customScenarios[activeScenarioId]?.name ?? DEMO_SCENARIOS[activeScenarioId]?.name ?? 'Custom'

  const total = useEventPlannerStore(selectTotal)
  const unprocessed = useEventPlannerStore(selectUnprocessed)
  const placed = useEventPlannerStore(selectPlacedCount)
  const overflow = useEventPlannerStore(selectOverflow)
  const capacity = useEventPlannerStore(selectCapacity)
  const peak = useEventPlannerStore(selectPeakConcurrent)

  const setAlgorithm = useEventPlannerStore((s) => s.setAlgorithm)
  const rearrange = useEventPlannerStore((s) => s.rearrange)
  const autoPlaceNext = useEventPlannerStore((s) => s.autoPlaceNext)
  const autoPlaceBlock = useEventPlannerStore((s) => s.autoPlaceBlock)
  const autoPlaceAll = useEventPlannerStore((s) => s.autoPlaceAll)
  const resetPlacement = useEventPlannerStore((s) => s.resetPlacement)
  const confirmAircraft = useEventPlannerStore((s) => s.confirmAircraft)
  const select = useEventPlannerStore((s) => s.select)
  const selectedAllocationId = useEventPlannerStore((s) => s.selectedAllocationId)

  // Render the ordered queue; cap rows for DOM sanity at large scales.
  const rows = useMemo(
    () => orderedScheduleIds.slice(0, 600).map((id) => inquiriesById[id]).filter(Boolean),
    [orderedScheduleIds, inquiriesById]
  )

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => onOpenChange(true)}
        className="pointer-events-auto absolute right-4 top-[96px] flex items-center gap-2 rounded-lg border border-white/10 bg-black/70 px-3 py-2 text-sm font-medium text-white/80 backdrop-blur-sm hover:text-white"
      >
        <Layers className="h-4 w-4" /> Planner ({unprocessed})
      </button>
    )
  }

  return (
    <div className="pointer-events-auto absolute right-0 top-0 flex h-full w-[360px] flex-col border-l border-white/10 bg-slate-950/85 backdrop-blur-md">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-white/10 px-3 py-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-white">Allocation Planner</span>
            <span className="rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-medium text-white/60">
              {scenarioName}
            </span>
          </div>
          <div className="text-[11px] text-white/50">
            {placed} placed ·{' '}
            <span
              className="text-sky-300"
              title="peak concurrent occupancy / total slot capacity (time-aware reuse)"
            >
              peak {peak}/{capacity}
            </span>{' '}
            ·{' '}
            {overflow > 0 && <span className="text-red-300">{overflow} overflow · </span>}
            <span className="text-amber-300">{unprocessed} unprocessed</span> · {total} total
          </div>
        </div>
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="flex h-7 w-7 items-center justify-center rounded text-white/50 hover:bg-white/10 hover:text-white"
          title="Collapse"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Controls */}
      <div className="space-y-3 border-b border-white/10 px-3 py-3">
        {/* Algorithm toggle */}
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wide text-white/40">Order by</span>
          <div className="flex overflow-hidden rounded-md border border-white/10">
            {(['arrival', 'departure'] as PlacementAlgorithm[]).map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => setAlgorithm(a)}
                className={cn(
                  'flex items-center gap-1 px-2 py-1 text-[11px] font-medium capitalize',
                  algorithm === a ? 'bg-sky-500/30 text-sky-200' : 'text-white/50 hover:bg-white/5'
                )}
              >
                {a === 'arrival' ? (
                  <PlaneLanding className="h-3 w-3" />
                ) : (
                  <PlaneTakeoff className="h-3 w-3" />
                )}
                {a}
              </button>
            ))}
          </div>
        </div>

        {algorithmDirty && (
          <button
            type="button"
            onClick={rearrange}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-amber-500/20 px-3 py-1.5 text-xs font-semibold text-amber-200 hover:bg-amber-500/30"
          >
            <Wand2 className="h-3.5 w-3.5" /> Rearrange ({algorithm})
          </button>
        )}

        {/* Auto-place actions */}
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={autoPlaceNext}
            disabled={unprocessed === 0}
            className="rounded-md bg-white/10 px-2 py-1.5 text-xs font-medium text-white/80 hover:bg-white/20 disabled:opacity-40"
          >
            Place next
          </button>
          <div className="flex items-stretch gap-1">
            <button
              type="button"
              onClick={() => autoPlaceBlock(blockSize)}
              disabled={unprocessed === 0}
              className="flex-1 rounded-md bg-white/10 px-2 py-1.5 text-xs font-medium text-white/80 hover:bg-white/20 disabled:opacity-40"
            >
              Block
            </button>
            <select
              value={blockSize}
              onChange={(e) => setBlockSize(Number(e.target.value))}
              className="rounded-md border border-white/10 bg-slate-900 px-1 text-xs text-white/70"
            >
              {[10, 15, 20].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={autoPlaceAll}
            disabled={unprocessed === 0}
            className="flex items-center justify-center gap-1.5 rounded-md bg-sky-500/30 px-2 py-1.5 text-xs font-semibold text-sky-100 hover:bg-sky-500/40 disabled:opacity-40"
          >
            <Zap className="h-3.5 w-3.5" /> Place all
          </button>
          <button
            type="button"
            onClick={resetPlacement}
            disabled={placed === 0}
            className="flex items-center justify-center gap-1.5 rounded-md bg-white/5 px-2 py-1.5 text-xs font-medium text-white/60 hover:bg-white/10 disabled:opacity-40"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Reset
          </button>
        </div>
        {/* Zones toggle */}
        <button
          type="button"
          onClick={() => setShowZones((v) => !v)}
          disabled={placed === 0}
          className="flex w-full items-center justify-center gap-1.5 rounded-md bg-white/5 px-2 py-1 text-[11px] font-medium text-white/60 hover:bg-white/10 disabled:opacity-40"
        >
          <Layers className="h-3 w-3" /> {showZones ? 'Hide zones' : 'Confirm by zone'}
        </button>
      </div>

      {/* Zone confirm list (collapsible) */}
      {showZones && (
        <div className="max-h-48 overflow-y-auto border-b border-white/10 bg-white/[0.02]">
          <ZoneConfirmList />
        </div>
      )}

      {/* List */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {!ready ? (
          <div className="px-3 py-6 text-center text-xs text-white/40">Loading inquiries…</div>
        ) : (
          rows.map((inq) => {
            const id = allocId(inq.scheduleId)
            const p = placements[id]
            return (
              <ArrivalInquiryRow
                key={inq.scheduleId}
                inquiry={inq}
                placed={!!p}
                confirmed={!!p?.confirmed}
                selected={selectedAllocationId === id}
                onConfirm={() => confirmAircraft(id)}
                onSelect={() => select(id)}
              />
            )
          })
        )}
        {ready && orderedScheduleIds.length > rows.length && (
          <div className="px-3 py-3 text-center text-[10px] text-white/30">
            Showing {rows.length} of {orderedScheduleIds.length}
          </div>
        )}
      </div>
    </div>
  )
}
